import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import blogPosts from '../content/blogPosts';
import api from '../api/api';
import { useUser } from '../context/UserContext';
import formatDateTime from '../helpers/formatDateTime';
import './blog.css';

const formatDate = (dateString) => {
    const d = new Date(dateString);
    return d.toLocaleDateString('en-US', {
        month: 'long',
        day: 'numeric',
        year: 'numeric'
    });
};

const BlogPostPage = () => {
    const { slug } = useParams();
    const { user } = useUser();
    const post = blogPosts.find((p) => p.slug === slug);
    const [comments, setComments] = useState([]);
    const [authorName, setAuthorName] = useState('');
    const [content, setContent] = useState('');
    const [message, setMessage] = useState({ type: '', text: '' });
    const [loading, setLoading] = useState(false);

    const loadComments = async () => {
        if (!slug) return;
        setLoading(true);
        try {
            const res = await api.get(`/blog/comments/${slug}`);
            setComments(res?.data || []);
        } catch (e) {
            setMessage({ type: 'error', text: 'Unable to load comments right now.' });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadComments();
    }, [slug]);

    const submitComment = async () => {
        const trimmedContent = content.trim();
        const trimmedName = authorName.trim();

        if (!trimmedContent) {
            setMessage({ type: 'error', text: 'Please add a comment before posting.' });
            return;
        }

        if (!user && !trimmedName) {
            setMessage({ type: 'error', text: 'Please provide your name.' });
            return;
        }

        try {
            await api.post('/blog/comments', {
                postSlug: slug,
                authorName: trimmedName,
                content: trimmedContent
            });
            setContent('');
            setMessage({ type: 'success', text: 'Thanks for your comment.' });
            loadComments();
        } catch (e) {
            const backendError = e?.response?.data?.error;
            setMessage({ type: 'error', text: backendError || 'Unable to post your comment.' });
        }
    };

    if (!post) {
        return (
            <div className="blog-shell">
                <div className="blog-not-found">
                    <h2>Post not found</h2>
                    <p>The article you requested is not available.</p>
                    <Link to="/blog" className="blog-read-link">Back to Blog</Link>
                </div>
            </div>
        );
    }

    return (
        <div className="blog-shell">
            <article className="blog-post-card">
                <Link to="/blog" className="blog-back-link">← Back to Blog</Link>
                <div className="blog-card-meta">
                    <span>{post.category}</span>
                    <span>{formatDate(post.date)}</span>
                    <span>{post.readTime}</span>
                </div>
                <h1>{post.title}</h1>
                {post.body.map((paragraph, idx) => (
                    <p key={idx}>{paragraph}</p>
                ))}
                {Array.isArray(post.references) && post.references.length > 0 && (
                    <div className="blog-references">
                        <h4>References</h4>
                        <ul>
                            {post.references.map((ref, idx) => (
                                <li key={`${ref.url}-${idx}`}>
                                    <a href={ref.url} target="_blank" rel="noreferrer">{ref.label}</a>
                                </li>
                            ))}
                        </ul>
                    </div>
                )}
            </article>

            <section className="blog-comments-card">
                <h3>Comments</h3>
                <p className="blog-comments-subtitle">Join the conversation and share your perspective.</p>

                {message.text && (
                    <div className={`app-notice ${message.type === 'success' ? 'app-notice-success' : 'app-notice-error'}`}>
                        {message.text}
                    </div>
                )}

                <div className="blog-comment-form">
                    {!user && (
                        <input
                            type="text"
                            placeholder="Your name"
                            value={authorName}
                            onChange={(e) => setAuthorName(e.target.value)}
                        />
                    )}
                    <textarea
                        placeholder="Write your comment"
                        value={content}
                        onChange={(e) => setContent(e.target.value)}
                        rows={4}
                    />
                    <button type="button" className="blog-comment-btn" onClick={submitComment}>Post Comment</button>
                </div>

                <div className="blog-comments-list">
                    {!loading && comments.length === 0 && (
                        <p className="blog-empty-comments">No comments yet. Be the first to share your thoughts.</p>
                    )}
                    {comments.map((comment) => (
                        <article key={comment.id} className="blog-comment-item">
                            <div className="blog-comment-head">
                                <strong>{comment.author_name || comment.authorName}</strong>
                                <span>{comment.created_at ? formatDateTime(comment.created_at) : ''}</span>
                            </div>
                            <p>{comment.content}</p>
                        </article>
                    ))}
                </div>
            </section>
        </div>
    );
};

export default BlogPostPage;
